module Hereabout.UI.Components.VenueFeedComponent where

import Prelude

import Data.Array as A
import Data.Maybe (Maybe(..))
import Data.String as S
import Effect.Class (class MonadEffect)
import Halogen (ClassName(..))
import Halogen as H
import Halogen.HTML (address)
import Halogen.HTML as HH
import Halogen.HTML.Events as HE
import Halogen.HTML.Properties as HP
import Hereabout.Effects (class Navigate, class ShareURL)
import Hereabout.Types (Address(..), Event(..), VenueFeed(..))
import Hereabout.UI.Bits (renderIcon)
import Hereabout.UI.Bulma.Classes (grid)
import Hereabout.UI.Bulma.Classes as Bulma
import Hereabout.UI.Components.ContentComponent as ContentComp
import Hereabout.UI.Components.EventPreviewComponent as EventPrvComp
import Hereabout.UI.SemanticUI.Classes as SemUI
import Type.Proxy (Proxy(..))

data Action
  = Increment

type Slot id = forall q. H.Slot q Void id

type ChildSlots =
  ( eventPreviews :: EventPrvComp.Slot Int
  , contentComp :: ContentComp.Slot Unit
  )

component :: forall q o m. MonadEffect m => Navigate m => ShareURL m => H.Component q VenueFeed o m
component =
  H.mkComponent
    { initialState: \s -> s
    , render
    , eval: H.mkEval H.defaultEval { handleAction = handleAction }
    }

render :: forall m. MonadEffect m => Navigate m => ShareURL m => VenueFeed -> H.ComponentHTML Action ChildSlots m
render (VenueFeed cf) =
  HH.div []
    [ renderLocation (VenueFeed cf)
    , renderEvents cf.events
    ]

addressInLine :: Address -> String
addressInLine (Address address) =
  S.joinWith ", " $ 
    [address.addressLine1]
      <> A.fromFoldable (address.addressLine2)
      <> A.fromFoldable (address.addressLine3)
      <> [address.addressLocality]
      <> A.fromFoldable (address.addressCounty)
      <> [address.addressPostalCode]
      <> A.fromFoldable (address.addressCountry)

renderLocation :: forall m. MonadEffect m => VenueFeed -> H.ComponentHTML Action ChildSlots m
renderLocation (VenueFeed vf) = 
  HH.div [ HP.classes [Bulma.block] ] $
    maybeImage
      <> 
      [ HH.h1 [ HP.classes [ Bulma.title ] ] [ HH.text vf.name ]
      , HH.p_ [renderIcon "fa-building" (HH.text $ addressInLine vf.address)]
      ] <> A.fromFoldable ( map (\pn -> HH.p_ [ renderIcon "fa-phone" (HH.a [ HP.href $ "tel:" <> pn ] [HH.text pn]) ] )  vf.phoneNumber )
        <> A.fromFoldable ( map (\s -> HH.p_ [ renderIcon "fa-globe" (HH.a [HP.href s] [HH.text s]) ] )  vf.website )
        <>  [ HH.div [ HP.classes [ Bulma.block, Bulma.mt3 ] ] 
              [ HH.slot_ (Proxy :: _ "contentComp") unit ContentComp.component vf.content
              ]
            ]
  where 
    maybeImage =
      case vf.image of
        Just imSrc -> 
          [ HH.div [ HP.classes [ Bulma.cardImage, Bulma.hasTextCentred ] ]
            [ HH.figure [ HP.classes [ Bulma.image, Bulma.isInlineBlock ] ] 
              [ HH.img [ HP.src imSrc, HP.style "max-height:700px" ]
              ]
            ]
          ]
        Nothing -> []

renderEvents :: forall m. MonadEffect m  => Navigate m => ShareURL m => Array Event -> H.ComponentHTML Action ChildSlots m
renderEvents events | A.length events > 0 = 
  HH.div [ HP.classes [Bulma.block] ] 
    [ HH.div [ HP.classes [ Bulma.fixedGrid, Bulma.has3ColsDesktop, Bulma.has1ColsMobile, Bulma.has2ColsTablet ]  ]
      [ HH.div [ HP.classes [ Bulma.grid ] ] $
        A.mapWithIndex (\i item ->
          HH.div [ HP.classes [ Bulma.cell ] ]
          [ HH.slot_ (Proxy :: _ "eventPreviews") i EventPrvComp.component item
          ]
        ) events
      ]
    ]
renderEvents _ = 
  HH.div [ HP.classes [Bulma.block] ]
    [ HH.text "No events available in this venue just yet. Please stay tuned for updates!"
    ]

handleAction :: forall cs o m. Action → H.HalogenM VenueFeed Action cs o m Unit
handleAction = case _ of
  Increment -> H.modify_ identity

